import {
  DocumentationRequiredType,
  getAllDocumentationRequiredByLoanId,
  patchUploadFileToDocumentationRequired,
  RequiredFile,
} from '@nemuru-eng/js-api-client';
import { useMemo, useState } from 'react';

import { DocumentationUploadFile } from '@/components/modules/Documentation/components/DocumentationDetails/components/UploadFileManager/types/DocumentationUploadFile';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { convertFileToBase64 } from '@/utils/convertFileToBase64';

interface UseUploadFileProps {
  loanId: string;
  documentationRequiredId: string;
  requiredFile: RequiredFile;
  documentationRequiredType: DocumentationRequiredType;
}

export const useUploadFile = ({
  loanId,
  documentationRequiredId,
  requiredFile,
  documentationRequiredType,
}: UseUploadFileProps) => {
  const [documentationToUpload, setDocumentationToUpload] = useState<DocumentationUploadFile[]>([]);

  const { request, loading: requestLoading } = useRequest();
  const { revalidate } = useCache(getAllDocumentationRequiredByLoanId, { loanId }, { revalidateOnMount: false });

  const onUploadDocumentation = async () => {
    if (!loanId || documentationToUpload.length === 0) return;
    await request(patchUploadFileToDocumentationRequired, {
      id: documentationRequiredId,
      documentationRequiredType: documentationRequiredType,
      files: documentationToUpload.map((file) => ({
        name: file.name,
        type: requiredFile.name,
        data: file.result,
      })),
    });
    setDocumentationToUpload([]);
    setTimeout(() => {
      revalidate();
    }, 1000);
  };

  const addFile = async (e, document) => {
    const value = e.target.files[0];
    if (value) {
      const base64File = await convertFileToBase64(value);
      setDocumentationToUpload([
        ...documentationToUpload,
        {
          result: base64File,
          file: value,
          name: value.name,
          size: value.size,
          mimeType: value.type,
          document: document,
        },
      ]);
    }
  };

  const removeFile = (fileName: string) => {
    if (documentationToUpload.length > 0) {
      const newFiles = documentationToUpload.filter((file) => file.name !== fileName);
      setDocumentationToUpload(newFiles);
    }
  };

  const fileNameAlreadyUsed = useMemo(() => {
    const valuesSet = new Set(requiredFile.uploadedFiles?.map((obj) => obj['name']));
    const willFileBeOverwritten = documentationToUpload.some((obj) => valuesSet.has(obj['name']));
    return willFileBeOverwritten;
  }, [documentationToUpload, requiredFile.uploadedFiles]);

  return {
    documentationToUpload,
    addFile,
    removeFile,
    onUploadDocumentation,
    fileNameAlreadyUsed,
    requestLoading,
  };
};
